/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import qs from 'qs';
import * as R from 'ramda';
import http from 'utils/http';
import { setErrorWithoutSnack } from 'utils/helpers/fetch';
import { createSnack } from 'snackbar/snackbarSlice';

const initialState = {
  command: {
    productId: null,
    productTransferOrderId: null,
    recipientIdName: '',
    referenceId: '',
    rvp: '',
    transferDate: '',
    volume: '',
  },
  dataCompany: '',
  dataCompanyId: null,
  errorMessage: '',
  inventorySelection: undefined,
  location: '',
  locationId: null,
  originator: '',
  originatorIdName: '',
  pending: false,
  productTransferOrderId: null,
  reconsignmentControl: undefined,
  response: {},
  sendError: '',
  sendPending: false,
  status: '',
};

const reducers = {
  fetchEditPtoDetailsError(state, { payload }) {
    return R.assoc('errorMessage', payload.error, state);
  },
  fetchEditPtoDetailsPending(state, { payload }) {
    return R.assoc('pending', payload, state);
  },
  fetchEditPtoDetailsSuccess(state, { payload }) {
    Object.keys(payload).forEach(key => {
      state[key] = payload[key];
    });
  },
  resetEditPto() {
    return initialState;
  },
  sendEditPtoDetailsError(state, { payload }) {
    return R.assoc('sendError', payload.error, state);
  },
  sendEditPtoDetailsPending(state, { payload }) {
    return R.assoc('statePending', payload, state);
  },
  sendEditPtoDetailsResetError(state) {
    return R.assoc('sendError', '', state);
  },
  sendEditPtoDetailsSuccess(state, { payload }) {
    return R.assoc('response', payload, state);
  },
  setCommand(state, { payload }) {
    return R.assoc('command', payload, state);
  },
  updateCommand: {
    prepare: (key, value) => ({
      payload: { key, value },
    }),
    reducer(state, { payload }) {
      const { key, value } = payload;
      state.command[key] = value;
    },
  },
  updateErrors(state, { payload }) {
    Object.keys(payload).forEach(key => {
      state.errorFields[key] = payload[key];
    });
  },
};

const { actions, reducer } = createSlice({
  initialState,
  name: 'editPtoDetails',
  reducers,
});

export const {
  fetchEditPtoDetailsPending,
  fetchEditPtoDetailsSuccess,
  fetchEditPtoDetailsError,
  resetEditPto,
  sendEditPtoDetailsPending,
  sendEditPtoDetailsSuccess,
  setCommand,
  sendEditPtoDetailsError,
  updateCommand,
  updateErrors,
  sendEditPtoDetailsResetError,
} = actions;

// Actions
let fetchController;
export function fetchEditPtoDetails(queryParams = {}) {
  return dispatch => {
    const paramString = qs.stringify(queryParams, {
      allowDots: true,
      encode: false,
    });
    dispatch(fetchEditPtoDetailsPending(true));
    if (fetchController) fetchController.abort();
    fetchController = new AbortController();
    const { signal } = fetchController;
    return http
      .get(`/product-transfer-orders/edit?${paramString}`, { signal })
      .then(res => {
        dispatch(fetchEditPtoDetailsSuccess(res.data));
        dispatch(fetchEditPtoDetailsPending(false));
        dispatch(fetchEditPtoDetailsError(''));
      })
      .catch(error => {
        dispatch(
          createSnack({
            message: error.response.data.title,
            options: { variant: 'error' },
          })
        );
        setErrorWithoutSnack(error, dispatch, fetchEditPtoDetailsError);
        dispatch(fetchEditPtoDetailsPending(false));
      });
  };
}

let editController;
export function sendEditPtoDetails(ptoNumber) {
  return (dispatch, getState) => {
    const {
      productId,
      recipientIdName,
      referenceId,
      rvp,
      transferDate: editPtoTransferDate,
      volume: editPtoVolume,
    } = getState().pto.editPtoDetails.command;

    const params = {
      productId,
      productTransferOrderId: ptoNumber,
      recipientIdName,
      referenceId,
      rvp,
      transferDate: editPtoTransferDate,
      volume: parseFloat(editPtoVolume),
    };
    dispatch(sendEditPtoDetailsPending(true));
    if (editController) editController.abort();
    editController = new AbortController();
    const { signal } = editController;
    return http
      .post('/product-transfer-orders/edit', params, { signal })
      .then(res => {
        dispatch(sendEditPtoDetailsSuccess(res.data));
        dispatch(sendEditPtoDetailsPending(false));
      })
      .catch(error => {
        setErrorWithoutSnack(error, dispatch, sendEditPtoDetailsError);
      });
  };
}

export function updateEditPtoField(key, value, integerOnly = false) {
  return dispatch => {
    dispatch(updateCommand(key, integerOnly ? value.replace(/[^0-9]/g, '') : value));
  };
}

export function resetSendEditPtoError() {
  return dispatch => {
    dispatch(sendEditPtoDetailsResetError());
  };
}

export default reducer;
